import React, { useCallback, useEffect, useState } from 'react';
import { researchosService } from '../../services/researchosService';
import RelevanceEditor from './RelevanceEditor';
import OfficialChannelControl from './OfficialChannelControl';

const EMPTY_REL = { sector_codes: [], sub_industry_codes: [], tickers: [] };

// Superuser-only: CHANNEL-LEVEL coverage (DB-derived truth), distinct from the per-run event log.
// Shows every channel we've stored anything for — even ones backfilled before the Runs UI existed —
// and, per window, how many videos exist / are stored / are still missing, with a fill button.
const GOLD = 'rgb(var(--brand-gold-rgb))';

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toISOString().slice(0, 10); } catch { return s; }
}

const WINDOWS = [
  { v: '1', label: '1 year' },
  { v: '2', label: '2 years' },
  { v: '3', label: '3 years' },
  { v: 'all', label: 'All time' },
];

export default function CoveragePanel() {
  const [channels, setChannels] = useState([]);
  const [win, setWin] = useState('1');
  const [cov, setCov] = useState({});         // source_ref -> coverage result (or {loading|error})
  const [busyFill, setBusyFill] = useState({});
  const [manualUrl, setManualUrl] = useState('');
  const [manualName, setManualName] = useState('');
  const [editRel, setEditRel] = useState(null);   // {channel_id, name, value, loading, saving}
  const [official, setOfficial] = useState({});   // channel_id -> {key,ticker,name} (declared)
  const [suggest, setSuggest] = useState({});     // channel_id -> {key,ticker,name,tradeable} (exact-name auto-match, NOT applied until confirmed)

  const openEdit = async (c) => {
    setEditRel({ channel_id: c.channel_id, name: c.source_ref, value: EMPTY_REL, loading: true });
    try {
      const r = await researchosService.getChannelRelevance(c.channel_id);
      setEditRel((e) => e && { ...e, loading: false, value: {
        sector_codes: r.data.sector_codes || [],
        sub_industry_codes: r.data.sub_industry_codes || [],
        tickers: r.data.tickers || [] } });
    } catch { setEditRel((e) => e && { ...e, loading: false }); }
  };
  const saveEdit = async () => {
    setEditRel((e) => e && { ...e, saving: true });
    try {
      await researchosService.saveChannelRelevance({ channel_id: editRel.channel_id, ...editRel.value });
      setEditRel(null);
    } catch { setEditRel((e) => e && { ...e, saving: false }); }
  };

  const loadChannels = useCallback(async () => {
    try { setChannels((await researchosService.getChannels()).data || []); } catch { /* ignore */ }
    try {
      const r = await researchosService.getOfficialChannels();
      const m = {}, sg = {};
      (r.data?.declared || []).forEach((o) => { if (o.channel_id) m[o.channel_id] = o.official_of; });
      (r.data?.suggestions || []).forEach((o) => { if (o.channel_id) sg[o.channel_id] = o.entity; });
      setOfficial(m); setSuggest(sg);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadChannels(); }, [loadChannels]);

  // Re-checking a window invalidates prior coverage (it was computed for the old window).
  useEffect(() => { setCov({}); }, [win]);

  // Prefer the channel_url stamped on the docs at ingest; fall back to inferring from a handle-like
  // display name; else empty (→ the manual by-URL check below).
  const urlFor = (c) => {
    if (c.channel_url) return c.channel_url;
    const ref = c.source_ref || '';
    if (/^https?:\/\//i.test(ref)) return ref;
    if (ref.startsWith('@')) return `https://www.youtube.com/${ref}`;
    return '';
  };

  const check = async (key, url, name) => {
    if (!url) { setCov((c) => ({ ...c, [key]: { error: 'No channel URL — use the manual check below.' } })); return; }
    setCov((c) => ({ ...c, [key]: { loading: true } }));
    try {
      const r = await researchosService.getChannelCoverage({ channel_url: url, years: win });
      setCov((c) => ({ ...c, [key]: { ...r.data, _url: url, _name: name } }));
    } catch (e) {
      setCov((c) => ({ ...c, [key]: { error: e?.response?.data?.error || e.message } }));
    }
  };

  const fill = async (key, url, name, missing) => {
    if (!url) return;
    setBusyFill((b) => ({ ...b, [key]: true }));
    try {
      await researchosService.triggerBackfill({ channel_url: url, name: name || undefined, years: win });
      setCov((c) => ({ ...c, [key]: { ...(c[key] || {}), _queued: missing } }));
    } catch { /* surfaced by Runs tab */ } finally {
      setBusyFill((b) => ({ ...b, [key]: false }));
    }
  };

  const CoverageDetail = ({ ck }) => {
    const d = cov[ck];
    if (!d) return null;
    if (d.loading) return <span className="text-xs text-gray-400">enumerating…</span>;
    if (d.error) return <span className="text-xs text-red-500">{d.error}</span>;
    const total = d.in_window_total ?? 0;
    const stored = d.in_window_stored ?? 0;
    const missing = d.in_window_missing ?? 0;
    const pct = total ? Math.round((100 * stored) / total) : 0;
    return (
      <div className="mt-1 w-full">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span className="tabular-nums text-gray-700 dark:text-gray-200">{total} in window</span>
          <span className="tabular-nums text-green-600 dark:text-green-400">{stored} stored</span>
          <span className={`tabular-nums ${missing > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}>{missing} missing</span>
          <span className="tabular-nums text-gray-400">· {d.enumerated_total} all-time · {d.older_than_window} older</span>
          {d._queued != null && <span className="text-green-600 dark:text-green-400">✓ queued {d._queued} — watch the Runs tab</span>}
          {missing > 0 && d._queued == null && (
            <button onClick={() => fill(ck, d._url, d._name, missing)} disabled={busyFill[ck]}
              className="rounded px-2 py-0.5 text-[11px] font-medium disabled:opacity-50"
              style={{ color: GOLD, border: `1px solid ${GOLD}` }}>
              {busyFill[ck] ? 'Queuing…' : `Backfill ${missing} missing`}
            </button>
          )}
        </div>
        <div className="mt-1 h-1.5 w-full max-w-md overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
          <div className="h-full rounded" style={{ width: `${pct}%`, background: GOLD }} />
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider text-gray-500">Window</span>
        {WINDOWS.map((w) => (
          <button key={w.v} onClick={() => setWin(w.v)}
            className="rounded px-2.5 py-1 text-xs"
            style={win === w.v ? { color: GOLD, border: `1px solid ${GOLD}`, fontWeight: 500 }
              : { border: '1px solid transparent', color: '#6b7280' }}>
            {w.label}
          </button>
        ))}
        <span className="ml-2 text-xs text-gray-400">Coverage is live from the DB — “Check” enumerates the channel (no transcript fetch).</span>
      </div>

      {/* Channels already in the DB */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-800">
        <div className="border-b border-gray-100 px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 dark:border-gray-800/60">
          Channels ({channels.length})
        </div>
        {channels.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">No channels stored yet.</p>
        ) : channels.map((c) => {
          const ck = c.source_ref;
          const url = urlFor(c);
          return (
            <div key={ck} className="border-b border-gray-100 px-3 py-2 last:border-b-0 dark:border-gray-800/60">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="font-medium text-gray-900 dark:text-gray-100">{c.source_ref}</span>
                <span className="tabular-nums text-gray-500">{c.stored} stored</span>
                <span className="text-xs text-gray-400">{fmtDate(c.earliest)} → {fmtDate(c.latest)}</span>
                <div className="ml-auto flex items-center gap-2">
                  <OfficialChannelControl
                    channelId={c.channel_id}
                    declared={c.channel_id ? official[c.channel_id] : null}
                    suggestion={c.channel_id ? suggest[c.channel_id] : null}
                    onChange={(of) => setOfficial((m) => ({ ...m, [c.channel_id]: of }))} />
                  {c.channel_id && (
                    <button onClick={() => openEdit(c)}
                      className="rounded px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800/40"
                      style={{ border: '1px solid #d1d5db' }}>
                      Relevance
                    </button>
                  )}
                  <button onClick={() => check(ck, url, c.source_ref)}
                    className="rounded px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800/40"
                    style={{ border: '1px solid #d1d5db' }}>
                    Check coverage
                  </button>
                </div>
              </div>
              <CoverageDetail ck={ck} />
            </div>
          );
        })}
      </div>

      {/* Manual check — for a channel not yet in the DB, or one whose URL can't be inferred */}
      <div className="mt-5 rounded-lg border border-gray-200 p-4 dark:border-gray-800">
        <div className="mb-2 text-[11px] uppercase tracking-wider text-gray-500">Check any channel by URL</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[240px] flex-1">
            <input value={manualUrl} onChange={(e) => setManualUrl(e.target.value)} placeholder="https://www.youtube.com/@handle"
              className="block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
          </div>
          <div className="w-44">
            <input value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Display name (optional)"
              className="block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
          </div>
          <button onClick={() => check('__manual__', manualUrl.trim(), manualName.trim())}
            disabled={!manualUrl.trim()}
            className="rounded px-4 py-1.5 text-sm font-medium disabled:opacity-50"
            style={{ color: GOLD, border: `1px solid ${GOLD}` }}>
            Check
          </button>
        </div>
        <div className="mt-2"><CoverageDetail ck="__manual__" /></div>
      </div>

      {editRel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditRel(null)}>
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 text-sm font-medium text-gray-900 dark:text-gray-100">Relevance · {editRel.name}</div>
            {editRel.loading ? <div className="text-xs text-gray-400">Loading…</div>
              : <RelevanceEditor value={editRel.value} onChange={(v) => setEditRel((e) => e && { ...e, value: v })} />}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEditRel(null)} className="rounded px-3 py-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Cancel</button>
              <button onClick={saveEdit} disabled={editRel.saving || editRel.loading}
                className="rounded px-3 py-1 text-xs font-medium disabled:opacity-50" style={{ color: GOLD, border: `1px solid ${GOLD}` }}>
                {editRel.saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
